/**
 * Storage module for managing click_id and UTM parameters in localStorage
 */

import { log, warn } from './logger.js';

const STORAGE_KEY = 'rbly_click_id';
const STORAGE_KEY_UTM = 'rbly_utm_params';
const DEFAULT_URL_PARAM = 'rbly_click_id';

// Standard UTM parameters
const STANDARD_UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
];

/**
 * Get click_id from URL query parameters
 * @param {Object} [location=window.location] - Location object (defaults to window.location, can be overridden for testing)
 * @returns {string|null} click_id if found in URL, null otherwise
 */
function getClickIdFromUrl(location = (typeof window !== 'undefined' ? window.location : {}), clickIdParam = DEFAULT_URL_PARAM) {
  try {
    const urlParams = new URLSearchParams(location.search || '');
    return urlParams.get(clickIdParam);
  } catch (error) {
    warn('[Rebrandly SDK] Error reading URL parameters:', error);
    return null;
  }
}

/**
 * Get UTM parameters from URL query parameters
 * @param {Object} [location=window.location] - Location object (defaults to window.location, can be overridden for testing)
 * @param {Array<string>} [customParams=[]] - Additional UTM parameter names to capture
 * @returns {Object} Object containing UTM parameters found in URL
 */
function getUtmParamsFromUrl(location = (typeof window !== 'undefined' ? window.location : {}), customParams = []) {
  try {
    const urlParams = new URLSearchParams(location.search || '');
    const utmParams = {};

    // Combine standard and custom UTM parameters
    const allUtmParams = [...STANDARD_UTM_PARAMS, ...customParams];

    // Extract each UTM parameter if present
    allUtmParams.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmParams[param] = value;
      }
    });

    return Object.keys(utmParams).length > 0 ? utmParams : null;
  } catch (error) {
    warn('[Rebrandly SDK] Error reading UTM parameters:', error);
    return null;
  }
}

/**
 * Get verification token from URL query parameters
 * @param {string} [paramName='rbly_verify'] - The query parameter name to look for
 * @param {Object} [location=window.location] - Location object (defaults to window.location, can be overridden for testing)
 * @returns {string|null} Verification token if found in URL, null otherwise
 */
function getVerifyTokenFromUrl(paramName = 'rbly_verify', location = (typeof window !== 'undefined' ? window.location : {})) {
  try {
    const urlParams = new URLSearchParams(location.search || '');
    return urlParams.get(paramName);
  } catch (error) {
    warn('[Rebrandly SDK] Error reading verify parameter:', error);
    return null;
  }
}

/**
 * Get click_id from localStorage
 * @returns {string|null} click_id if found in storage, null otherwise
 */
function getClickIdFromStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    warn('[Rebrandly SDK] Error reading from localStorage:', error);
    return null;
  }
}

/**
 * Get UTM parameters from localStorage
 * @returns {Object|null} UTM parameters if found, null otherwise
 */
function getUtmParamsFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_UTM);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    warn('[Rebrandly SDK] Error reading UTM parameters from localStorage:', error);
    return null;
  }
}

/**
 * Save click_id to localStorage
 * @param {string} clickId - The click_id to store
 * @returns {boolean} true if successful, false otherwise
 */
function saveClickIdToStorage(clickId) {
  try {
    localStorage.setItem(STORAGE_KEY, clickId);
    return true;
  } catch (error) {
    warn('[Rebrandly SDK] Error saving to localStorage:', error);
    return false;
  }
}

/**
 * Save UTM parameters to localStorage associated with click ID
 * @param {string} clickId - The click ID these UTM params are associated with
 * @param {Object|null} utmParams - The UTM parameters to store
 * @returns {boolean} true if successful, false otherwise
 */
function saveUtmParamsToStorage(clickId, utmParams) {
  try {
    if (!utmParams || Object.keys(utmParams).length === 0) {
      // Clear stored UTM params if none provided
      localStorage.removeItem(STORAGE_KEY_UTM);
      return true;
    }

    const data = {
      clickId: clickId,
      utmParams: utmParams,
      timestamp: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY_UTM, JSON.stringify(data));
    return true;
  } catch (error) {
    warn('[Rebrandly SDK] Error saving UTM parameters to localStorage:', error);
    return false;
  }
}

/**
 * Initialize click_id and UTM parameter management
 * On every page load:
 * 1. Check URL for click_id parameter
 * 2. If found and different from stored value, replace it
 * 3. Also capture and store UTM parameters when click_id is found
 * 4. If not found, use existing value from localStorage
 * @param {Object} [location] - Location object (defaults to window.location, can be overridden for testing)
 * @param {Array<string>} [customUtmParams=[]] - Additional custom UTM parameter names to capture
 * @returns {string|null} The current click_id
 */
function initializeClickId(location, customUtmParams = [], clickIdParam = DEFAULT_URL_PARAM) {
  const urlClickId = getClickIdFromUrl(location, clickIdParam);
  const storedClickId = getClickIdFromStorage();

  // If click_id found in URL
  if (urlClickId) {
    // Capture UTM parameters from URL
    const utmParams = getUtmParamsFromUrl(location, customUtmParams);

    // Update click ID storage if it's different from stored value
    if (urlClickId !== storedClickId) {
      saveClickIdToStorage(urlClickId);
      log('[Rebrandly SDK] Click ID captured:', urlClickId);
    }

    // Always save UTM params when present (last-touch attribution)
    // This ensures we capture the most recent campaign data
    if (utmParams) {
      saveUtmParamsToStorage(urlClickId, utmParams);
      if (urlClickId !== storedClickId) {
        log('[Rebrandly SDK] UTM parameters captured:', utmParams);
      } else {
        log('[Rebrandly SDK] UTM parameters updated:', utmParams);
      }
    }

    return urlClickId;
  }

  // No click_id in URL, return stored value (may be null)
  return storedClickId;
}

/**
 * Get the current click_id (from storage)
 * @returns {string|null} The current click_id
 */
function getCurrentClickId() {
  return getClickIdFromStorage();
}

/**
 * Clear the stored click_id (useful for testing)
 */
function clearClickId() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    warn('[Rebrandly SDK] Error clearing localStorage:', error);
  }
}

export {
  initializeClickId,
  getCurrentClickId,
  clearClickId,
  getVerifyTokenFromUrl,
  getUtmParamsFromStorage,
  getUtmParamsFromUrl,
  saveUtmParamsToStorage,
  STORAGE_KEY,
  STORAGE_KEY_UTM,
  DEFAULT_URL_PARAM,
  STANDARD_UTM_PARAMS
};
